import * as React from 'react'
import { useMemo } from 'react'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'

import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import LaunchIcon from '@mui/icons-material/Launch'

import { ENTITY_TYPES } from '@sales-center/common'
import {
  addLeadFieldNameManageUrl,
  addToPestPacFooter,
  addToPestPacSettingsHeader,
  hideRequiredValues,
  systemOptionalFields,
} from '@user/components/Settings/EntityFormSettings/constants'
import { useFieldSettingsHandlers } from '@user/components/Settings/EntityFormSettings/hooks/useFieldSettingsHandlers'
import {
  combinesWithNextField,
  getFieldRenderer,
} from '@user/components/Settings/EntityFormSettings/config/fieldConfigs'
import {
  fieldOptions,
  titleValues,
  validationMessages,
} from '@user/components/Settings/EntityFormSettings/config/registryData'
import type { IEntityFormSetting } from '@user/store/entityFormSettings/typings'

import {
  PestPacOptionField,
  OpportunityStageField,
  NameRequirementModeField,
} from './fieldRenderers'
import { FieldSettingsCardContent } from './FieldSettingsCardContent'
import { getStyles } from './styling'

interface Props {
  settingItem: IEntityFormSetting
  sectionSettings: IEntityFormSetting[]
  allSettings: IEntityFormSetting[]
  entityType: ENTITY_TYPES
  isTidalThemeActive: boolean
}

/**
 * Determines how a card joins visually to the cards around it.
 *
 * Both flags derive from a single registry property, `combineWithNext`:
 *
 * - `isTopCombine` is the field's own `combineWithNext`, meaning it joins
 *   downward to the card below it.
 * - `isBottomCombine` is the *preceding* card's `combineWithNext`, meaning
 *   this card is joined to from above.
 *
 * "Preceding" is the immediately previous entry in `sectionSettings` — the
 * card that renders directly above this one, since that array is already
 * filtered to the section and sorted into display order. It is deliberately
 * not an index into the registry, which holds every field regardless of
 * section or visibility.
 *
 * @param settingItem The field setting being rendered.
 * @param sectionSettings The section's field settings, in display order.
 * @param entityType Which entity's field registry to resolve against.
 */
function getCombineState(
  settingItem: IEntityFormSetting,
  sectionSettings: IEntityFormSetting[],
  entityType: ENTITY_TYPES,
) {
  const isTopCombine = combinesWithNextField(entityType, settingItem.fieldId)

  const index = sectionSettings.findIndex(
    (setting) => setting.fieldId === settingItem.fieldId,
  )
  const previousFieldId =
    index > 0 ? sectionSettings[index - 1].fieldId : undefined
  const isBottomCombine = previousFieldId
    ? combinesWithNextField(entityType, previousFieldId)
    : false

  return { isTopCombine, isBottomCombine }
}

/**
 * A single field's settings card: its label, toggles, and any field-specific UI.
 *
 * Every theme-dependent style comes from `getStyles`, so one card covers both
 * theme states. The one remaining branch on the theme is structural rather
 * than stylistic: the Tidal layout groups the checkbox and label inside an
 * extra row so the "manage" button can be pushed right with
 * `justify-content: space-between`, whereas the legacy layout leaves the three
 * as siblings and pushes the button right with the label's own `mr: auto`.
 * Collapsing those into one tree would change the rendered element count for
 * one of the two themes.
 *
 * Fields needing bespoke UI are selected by the registry's `renderer`
 * property, which maps to a component in `fieldRenderers/` rather than being
 * branched on by field id here.
 */
export function FieldSettingsCard(props: Props) {
  const {
    settingItem,
    sectionSettings,
    allSettings,
    entityType,
    isTidalThemeActive,
  } = props
  const { required, active, fieldId, systemRequired } = settingItem

  const {
    onChangeRequired,
    onChangeEnabled,
    onChangeOption,
    onChangeNameMode,
    onChangeLocationOption,
    onChangeLocationEnabled,
  } = useFieldSettingsHandlers({ allSettings, settingItem, entityType })

  const navigate = useNavigate()

  const systemNonOptionalRequired = useMemo(
    () => systemRequired && !systemOptionalFields.find((x) => x === fieldId),
    [systemRequired, fieldId],
  )

  const { isTopCombine, isBottomCombine } = useMemo(
    () => getCombineState(settingItem, sectionSettings, entityType),
    [settingItem, sectionSettings, entityType],
  )

  const validationMessage = useMemo(
    () => validationMessages.find((row) => row.key == fieldId)?.message,
    [fieldId],
  )

  const hideRequiredSetting = useMemo(
    () => hideRequiredValues.includes(fieldId),
    [fieldId],
  )

  const titleValue = useMemo(
    () => titleValues.find((header) => header.key === fieldId)?.message,
    [fieldId],
  )

  const settingOptions = useMemo(
    () => fieldOptions?.find((field) => field.key === fieldId)?.values || [],
    [fieldId],
  )

  const theme = useTheme()
  const styles = getStyles(theme, isTidalThemeActive)

  const renderer = useMemo(
    () => getFieldRenderer(entityType, fieldId),
    [entityType, fieldId],
  )

  const checkbox = (
    <Checkbox
      onClick={(evt) => evt.stopPropagation()}
      onChange={onChangeEnabled}
      checked={active}
      color="primary"
      edge={styles.props.checkboxEdge}
      size="medium"
      disabled={systemNonOptionalRequired}
    />
  )

  const checkboxCell = (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={styles.checkboxWrapper}
    >
      {checkbox}
    </Stack>
  )

  const content = (
    <FieldSettingsCardContent
      onChangeRequired={onChangeRequired}
      required={required}
      settingItem={settingItem}
      systemNonOptionalRequired={systemNonOptionalRequired}
      hideRequired={hideRequiredSetting}
      isTidalThemeActive={isTidalThemeActive}
    />
  )

  const manageFunnelButton = addLeadFieldNameManageUrl[settingItem.fieldId] && (
    <Button
      color="primary"
      variant="text"
      onClick={() => navigate(addLeadFieldNameManageUrl[settingItem.fieldId])}
      disableElevation
      sx={styles.manageFunnelButton}
    >
      {styles.props.manageFunnelLabel}
      <LaunchIcon sx={styles.manageFunnelIcon} />
    </Button>
  )

  return (
    <Paper
      sx={styles.getPaperSx(isTopCombine, isBottomCombine)}
      variant="outlined"
    >
      {validationMessage && (
        <Typography mx={2} pt={2} variant="body2">
          {validationMessage}
        </Typography>
      )}

      {renderer === 'pestPacOption' ? (
        <PestPacOptionField
          settingItem={settingItem}
          systemNonOptionalRequired={systemNonOptionalRequired}
          titleValue={titleValue}
          addToPestPacSettingsHeader={addToPestPacSettingsHeader}
          addToPestPacFooter={addToPestPacFooter}
          settingOptions={settingOptions}
          onChangeLocationEnabled={onChangeLocationEnabled}
          onChangeLocationOption={onChangeLocationOption}
          isTidalThemeActive={isTidalThemeActive}
        />
      ) : isTidalThemeActive ? (
        <Stack direction="row" sx={styles.getRowSx(hideRequiredSetting)}>
          <Stack direction="row">
            {checkboxCell}
            {content}
          </Stack>
          {manageFunnelButton}
        </Stack>
      ) : (
        <Stack direction="row" sx={styles.getRowSx(hideRequiredSetting)}>
          {checkboxCell}
          {content}
          {manageFunnelButton}
        </Stack>
      )}

      {renderer === 'opportunityStage' && (
        <OpportunityStageField
          settingItem={settingItem}
          titleValue={titleValue}
          settingOptions={settingOptions}
          onChangeOption={onChangeOption}
          isTidalThemeActive={isTidalThemeActive}
        />
      )}

      {renderer === 'nameRequirementMode' && (
        <NameRequirementModeField
          settingItem={settingItem}
          allSettings={allSettings}
          entityType={entityType}
          onChangeNameMode={onChangeNameMode}
          isTidalThemeActive={isTidalThemeActive}
        />
      )}
    </Paper>
  )
}
FieldSettingsCard.displayName = 'FieldSettingsCard'
